/*
 * ENTRA APP REGISTRATION REQUIREMENTS
 * =====================================
 * To use the Azure Secure Score dashboard, the app registration in Entra ID must be
 * configured as follows:
 *
 * 1. Platform: Single-page application (SPA)
 *    - Redirect URI: the helpdesk origin (e.g. https://helpdesk.alulatechnologies.com)
 *
 * 2. API Permissions (delegated, admin consented):
 *    - Microsoft Graph  > User.Read
 *    - Microsoft Graph  > User.ReadBasic.All
 *    - Azure Service Management > user_impersonation
 *      (found under "APIs my organization uses" or the well-known app id
 *       797f4846-ba00-4fd7-ba43-dac1f8f63013)
 *
 * 3. No client secret is required — this uses delegated user authentication.
 *
 * 4. The signed-in user needs at minimum:
 *    - "Reader" RBAC on each subscription
 *    - "Security Reader" role on Microsoft.Security
 *
 * Entra client/tenant: src/authConfig.js (same registration as API AzureAd appsettings).
 * Redirect URI: window.location.origin via MSAL.
 */

import { authService } from './authService';

const ARM_BASE = 'https://management.azure.com';

const TRANSIENT_STATUSES = new Set([429, 500, 502, 503, 504]);
const MAX_RETRIES = 3;

function retryDelay(attempt, retryAfterMs) {
  // Respect Retry-After if provided, otherwise exponential backoff with jitter
  if (retryAfterMs) return retryAfterMs;
  return (2 ** attempt) * 1000 + Math.random() * 500;
}

class AzureSecurityService {
  async _fetchArm(url) {
    const token = await authService.getArmToken(false);
    let lastError;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) return res;
      if (!TRANSIENT_STATUSES.has(res.status)) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(`${res.status}: ${text}`);
      }
      const retryAfterHeader = res.headers.get('Retry-After');
      const retryAfterMs = retryAfterHeader ? parseFloat(retryAfterHeader) * 1000 : null;
      lastError = new Error(`${res.status}: ${res.statusText}`);
      if (attempt < MAX_RETRIES) {
        await new Promise(r => setTimeout(r, retryDelay(attempt, retryAfterMs)));
      }
    }
    throw lastError;
  }

  async _armGet(path, apiVersion) {
    const url = `${ARM_BASE}${path}?api-version=${apiVersion}`;
    const res = await this._fetchArm(url);
    return res.json();
  }

  async _armGetAllPages(path, apiVersion) {
    let results = [];
    let nextUrl = `${ARM_BASE}${path}?api-version=${apiVersion}`;
    while (nextUrl) {
      const res = await this._fetchArm(nextUrl);
      const data = await res.json();
      results = results.concat(data.value ?? []);
      nextUrl = data.nextLink ?? null;
    }
    return results;
  }

  /** Verify a silent ARM token can be acquired (no popup). Throws if consent needed. */
  async checkSilentAuth() {
    await authService.getArmToken(false);
  }

  /** Acquire ARM token interactively (popup). */
  async connectInteractive() {
    await authService.getArmToken(true);
  }

  async getSubscriptions() {
    const items = await this._armGetAllPages('/subscriptions', '2022-12-01');
    return items.filter(s => s.state === 'Enabled');
  }

  async getSecureScore(subId) {
    try {
      const data = await this._armGet(
        `/subscriptions/${subId}/providers/Microsoft.Security/secureScores/ascScore`,
        '2020-01-01'
      );
      return data.properties ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Fetch the tenant-wide assessment metadata catalogue.
   * Returns a map of { assessmentName -> properties } for enriching per-subscription assessments.
   * Call this once before the subscription loop — it is tenant-wide, not per-subscription.
   */
  async getAssessmentMetadataCatalogue() {
    const items = await this._armGetAllPages(
      '/providers/Microsoft.Security/assessmentMetadata',
      '2021-06-01'
    );
    return Object.fromEntries(items.map(item => [item.name, item.properties ?? {}]));
  }

  /**
   * Fetch unhealthy assessments for a subscription, enrich with metadata catalogue,
   * and group by assessment name so each unique recommendation type appears once
   * with all affected resources collected into a `resources` array.
   *
   * @param {string} subId
   * @param {Object} metaCatalogue - result of getAssessmentMetadataCatalogue()
   * @returns Array of grouped recommendation objects
   */
  async getUnhealthyAssessments(subId, metaCatalogue = {}) {
    const all = await this._armGetAllPages(
      `/subscriptions/${subId}/providers/Microsoft.Security/assessments`,
      '2021-06-01'
    );

    const unhealthy = all.filter(a => a.properties?.status?.code === 'Unhealthy');

    // Group by assessment name so each unique recommendation type appears once,
    // with all affected resources collected into a resources array.
    const groups = new Map();

    for (const a of unhealthy) {
      const meta = metaCatalogue[a.name] ?? {};
      const rd = a.properties?.resourceDetails ?? {};

      // Subscription-level recommendations have Source === 'Azure' and no meaningful resource name.
      const isSubLevel = (rd.Source ?? rd.source) === 'Azure';
      const resName = isSubLevel
        ? null
        : rd.resourceName || (rd.id ? rd.id.split('/').pop() : null) || null;

      if (!groups.has(a.name)) {
        const severity = meta.severity || a.properties?.metadata?.severity || 'Low';
        groups.set(a.name, {
          name: a.name,
          // Stable id at subscription scope — used by azurePortalUrl
          id: `/subscriptions/${subId}/providers/Microsoft.Security/assessments/${a.name}`,
          properties: {
            displayName: meta.displayName || a.properties?.displayName || a.name,
            metadata: {
              severity,
              description: meta.description || a.properties?.metadata?.description || null,
              remediationDescription:
                meta.remediationDescription ||
                a.properties?.metadata?.remediationDescription ||
                null,
              categories:
                meta.categories ||
                (meta.category ? [meta.category] : null) ||
                a.properties?.metadata?.categories ||
                [],
              resourceType:
                meta.resourceType ||
                a.properties?.metadata?.resourceType ||
                rd.resourceType ||
                null,
            },
            resources: [],
          },
        });
      }

      if (resName) {
        groups.get(a.name).properties.resources.push({
          id: rd.id ?? null,
          name: resName,
        });
      }
    }

    return Array.from(groups.values());
  }

  /**
   * Fetch attack paths for a subscription.
   * Requires Defender CSPM — subscriptions without it return an empty array, not an error.
   */
  /**
   * Fetch attack paths for a subscription via Azure Resource Graph (ARG).
   * The direct Microsoft.Security/attackPaths provider endpoint does not exist —
   * attack paths are only accessible through ARG KQL queries.
   * Requires Defender CSPM; subscriptions without it return an empty array.
   */
  async getAttackPaths(subId) {
    try {
      const token = await authService.getArmToken(false);
      let all = [];
      let skipToken = null;

      do {
        const body = {
          query: `securityresources | where type =~ 'microsoft.security/attackpaths' | where subscriptionId =~ '${subId}'`,
          subscriptions: [subId],
          ...(skipToken ? { options: { $skipToken: skipToken } } : {}),
        };

        const res = await fetch(
          'https://management.azure.com/providers/Microsoft.ResourceGraph/resources?api-version=2024-04-01',
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
          }
        );

        if (!res.ok) {
          const text = await res.text().catch(() => res.statusText);
          throw new Error(`${res.status}: ${text}`);
        }

        const data = await res.json();
        all = all.concat(data.data ?? []);
        skipToken = data.$skipToken ?? null;
      } while (skipToken);

      return all;
    } catch (err) {
      console.warn(`[AttackPaths] ${subId}:`, err.message);
      return [];
    }
  }
}

export const azureSecurityService = new AzureSecurityService();
